import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalDropdownField } from '../../../components/GlobalComponents/GlobalDropdownField';

interface TimetableOption {
  label: string;
  value: number;
  name: string;
  session: string;
}

interface TimetableTypeResponse {
  status: string;
  message?: string;
  data: Array<{
    id: number;
    name: string;
    session: string;
  }>;
}

const TimetableSettings: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [timetableOptions, setTimetableOptions] = useState<TimetableOption[]>([]);
  const [selectedTimetableId, setSelectedTimetableId] = useState<number | null>(null);

  useEffect(() => {
    fetchTimetableSettings();
  }, []);

  const fetchTimetableSettings = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await apiClient.get<TimetableTypeResponse>('/timetable-types');
      const responseData = response.data;

      if (responseData.status === 'success' && responseData.data) {
        const options: TimetableOption[] = responseData.data.map((item) => ({
          label: `${item.name} (${item.session})`,
          value: item.id,
          name: item.name,
          session: item.session,
        }));

        setTimetableOptions(options);

        if (options.length > 0 && !selectedTimetableId) {
          setSelectedTimetableId(options[0].value);
        }
      } else {
        throw new Error(responseData?.message || 'Invalid response format');
      }
    } catch (err: any) {
      console.error('Failed to fetch timetable settings:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to fetch timetable settings',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTimetableSettings = async (): Promise<void> => {
    if (!selectedTimetableId) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a timetable',
        life: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.post(
        `/timetable-types/${selectedTimetableId}/set-active`,
        {}
      );

      if (response.data.status === 'success') {
        const selectedOption = timetableOptions.find(
          (opt) => opt.value === selectedTimetableId
        );

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `Timetable updated successfully${selectedOption ? ` - ${selectedOption.label}` : ''}`,
          life: 4000,
        });
      } else {
        throw new Error(response.data.message || 'Failed to update timetable settings');
      }
    } catch (err: any) {
      console.error('Failed to save timetable settings:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to update timetable settings',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    fetchTimetableSettings();
  };

  if (loading) {
    return (
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }}
        ></div>
        <p style={{ color: '#64748b', fontSize: '14px' }}>Loading timetable settings...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '2px solid #f3f4f6',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="pi pi-calendar" style={{ fontSize: '18px', color: 'white' }}></i>
            </div>
            <div>
              <h2
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1e40af',
                  margin: 0,
                }}
              >
                Academic Timetable
              </h2>
              <p
                style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: '2px 0 0 0',
                }}
              >
                Select the active academic timetable for the current period
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <GlobalButton
              label=""
              onClick={handleRefresh}
              disabled={loading}
              icon="pi pi-refresh"
              variant="outline"
              size="small"
            />

            <GlobalButton
              label={saving ? 'Updating...' : 'Update Settings'}
              onClick={handleSaveTimetableSettings}
              disabled={saving || timetableOptions.length === 0}
              loading={saving}
              icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              variant="success"
              size="small"
            />
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: '500px' }}>
          <GlobalDropdownField
            label="Select Current Timetable"
            value={selectedTimetableId}
            onChange={(value) => setSelectedTimetableId(value)}
            options={timetableOptions}
            placeholder={timetableOptions.length === 0 ? 'No timetables available' : 'Select a timetable'}
            disabled={saving || timetableOptions.length === 0}
            fullWidth
            icon="pi pi-calendar"
          />

          <p
            style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '8px',
              lineHeight: 1.5,
            }}
          >
            {timetableOptions.length > 0 ? (
              <>
                {timetableOptions.length} option{timetableOptions.length !== 1 ? 's' : ''} available.
                Select the timetable that should be active for the current academic period.
              </>
            ) : (
              'No timetables available. Please create a timetable first.'
            )}
          </p>
        </div>
      </div>
    </>
  );
};

export default TimetableSettings;
